import React from 'react';
import { cn } from '@/lib/core/utils';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { AlertTriangle, Clock } from 'lucide-react';
import type { OvertimeWarning } from '@/hooks/kiosk';

interface OvertimeAlertProps {
  warning: OvertimeWarning | undefined;
  className?: string;
}

/**
 * Displays overtime warning banner
 */
export function OvertimeAlert({ warning, className }: OvertimeAlertProps) {
  if (!warning) return null;

  const isOvertime = !warning.approaching && warning.minutes_until_overtime === 0;

  if (isOvertime) {
    return (
      <Alert variant="destructive" className={cn('border-red-200 dark:border-red-500/40', className)}>
        <Clock className="h-5 w-5" />
        <AlertTitle className="text-lg font-semibold">Overtime Reached</AlertTitle>
        <AlertDescription>{warning.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div
      className={cn(
        'p-4 rounded-xl flex items-center gap-4',
        'bg-orange-50 dark:bg-orange-500/10',
        'border border-orange-200 dark:border-orange-500/40',
        className
      )}
    >
      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-orange-100 dark:bg-orange-500/20">
        <AlertTriangle className="w-6 h-6 text-orange-600 dark:text-orange-400" />
      </div>

      <div className="flex-1">
        <h3 className="font-semibold text-lg text-orange-700 dark:text-orange-400">
          Overtime Warning
        </h3>
        <p className="text-sm text-orange-600 dark:text-orange-300">
          {warning.message}
        </p>
      </div>

      {warning.approaching && (
        <div className="text-right flex-shrink-0">
          <p className="text-orange-700 dark:text-orange-400 text-2xl font-bold">
            {warning.minutes_until_overtime}
          </p>
          <p className="text-orange-600/70 dark:text-orange-400/70 text-xs">minutes left</p>
        </div>
      )}
    </div>
  );
}
